import React, { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { useMarkCompleteV3 } from 'hooks/useMarkCompleteV3'
import { selectors as contextSelectors } from 'auth/stores/userContext'
import { selectors as licenseSelectors } from 'store/licenseManager'
import { navigationEvent } from 'layers/navigation/store/operations'
import { isPreviewMode, isAdmin } from 'utils/highschoolHelpers'
import MarkCompleteSection from './component'
import {
  buildCompletionDateLine,
  buildSchoolCompletionCommentary,
  extractTextFromField,
  getHsActiveSites,
  getSchoolNameForActiveSite,
  resolveMarkCompleteButtonText,
  resolveMarkCompleteDoneText,
  shouldShowSchoolwideCompletionDetails,
} from './helpers'
import {
  SCHOOLWIDE_TRAINING_LESSON_TYPE,
  MARK_COMPLETE_ID,
  MARK_COMPLETE_HASH,
} from './constants'

const MarkCompleteSectionContainer = ({
  completionButtonLabelDefaultAdmin,
  completionButtonLabelDefaultAdminRef,
  completionButtonLabelDefaultEducator,
  completionButtonLabelDefaultEducatorRef,
  completionDateDefault,
  completionDateDone,
  completionHeader,
  completionLabelDone,
  completionLabelDoneRef,
  completionSchool,
  contentGroupCode,
  courseNodeId,
  coursePointer,
  instructorDataKey,
  nextStepButtonLabel,
  nextStepButtonLabelRef,
  nextStepHeader,
  nextStepHeaderRef,
  nextStepRoute,
  nextStepRouteRef,
  refreshInstructorWithGet,
  siteId: explicitSiteId,
}) => {
  const dispatch = useDispatch()
  const userContext = useSelector(contextSelectors.selectProgramContext) || {}
  const activeSites = useSelector(licenseSelectors.selectActiveSites) || []

  const hsActiveSites = useMemo(() => getHsActiveSites(activeSites), [
    activeSites,
  ])

  const {
    isComplete,
    completedAt,
    handleMarkComplete,
    lessonType,
  } = useMarkCompleteV3({
    coursePointer,
    courseNodeId,
    contentGroupCode,
    instructorDataKey,
    refreshInstructorWithGet,
    siteId: explicitSiteId,
  })

  const showSchoolwideCompletionDetails = useMemo(
    () => shouldShowSchoolwideCompletionDetails(lessonType),
    [lessonType],
  )

  const schoolName = useMemo(() => {
    if (!showSchoolwideCompletionDetails) return null
    return getSchoolNameForActiveSite(
      hsActiveSites,
      userContext?.hsActiveSiteId,
    )
  }, [
    showSchoolwideCompletionDetails,
    hsActiveSites,
    userContext?.hsActiveSiteId,
  ])

  const completionCommentaryTodo = useMemo(
    () =>
      showSchoolwideCompletionDetails
        ? buildSchoolCompletionCommentary({
            completionSchool,
            schoolName,
            dateLine: completionDateDefault || '',
          })
        : null,
    [
      showSchoolwideCompletionDetails,
      completionSchool,
      schoolName,
      completionDateDefault,
    ],
  )

  const completionCommentaryComplete = useMemo(() => {
    if (!showSchoolwideCompletionDetails || !schoolName) return null

    return buildSchoolCompletionCommentary({
      completionSchool,
      schoolName,
      dateLine: buildCompletionDateLine({
        datePrefix: completionDateDone,
        completedAt,
      }),
    })
  }, [
    showSchoolwideCompletionDetails,
    schoolName,
    completionSchool,
    completionDateDone,
    completedAt,
  ])

  useEffect(() => {
    if (window?.location?.hash !== MARK_COMPLETE_HASH) return undefined

    const timeoutId = setTimeout(() => {
      document.getElementById(MARK_COMPLETE_ID)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }, 100)

    return () => clearTimeout(timeoutId)
  }, [])

  const hasPermissionsToComplete =
    lessonType !== SCHOOLWIDE_TRAINING_LESSON_TYPE || isAdmin(userContext)

  const onMarkComplete = () => {
    // TODO: for Delivery 4, re-enable the ability to toggle complete back to false:
    if (isComplete || !hasPermissionsToComplete) return
    handleMarkComplete()
  }

  const handleClickNext = () => {
    dispatch(
      navigationEvent(extractTextFromField(nextStepRoute, nextStepRouteRef)),
    )
  }

  if (isPreviewMode()) {
    return null
  }

  const buttonText = resolveMarkCompleteButtonText({
    isAdminUser: isAdmin(userContext),
    completionButtonLabelDefaultAdmin,
    completionButtonLabelDefaultAdminRef,
    completionButtonLabelDefaultEducator,
    completionButtonLabelDefaultEducatorRef,
  })

  return (
    <MarkCompleteSection
      buttonText={buttonText}
      completeText={resolveMarkCompleteDoneText(
        completionLabelDone,
        completionLabelDoneRef,
      )}
      completionCommentaryComplete={completionCommentaryComplete}
      completionCommentaryTodo={completionCommentaryTodo}
      handleClickNext={handleClickNext}
      hasPermissionsToComplete={hasPermissionsToComplete}
      id={MARK_COMPLETE_ID}
      isComplete={isComplete}
      nextSectionButtonText={extractTextFromField(
        nextStepButtonLabel,
        nextStepButtonLabelRef,
      )}
      nextSectionPromptText={extractTextFromField(
        nextStepHeader,
        nextStepHeaderRef,
      )}
      onMarkComplete={onMarkComplete}
      promptText={completionHeader}
      shoulDisplayCompletionDetails={showSchoolwideCompletionDetails}
    />
  )
}

MarkCompleteSectionContainer.propTypes = {
  completionButtonLabelDefaultAdmin: PropTypes.string,
  completionButtonLabelDefaultAdminRef: PropTypes.array,
  completionButtonLabelDefaultEducator: PropTypes.string,
  completionButtonLabelDefaultEducatorRef: PropTypes.array,
  completionDateDefault: PropTypes.string,
  completionDateDone: PropTypes.string,
  completionHeader: PropTypes.string,
  completionLabelDone: PropTypes.string,
  completionLabelDoneRef: PropTypes.array,
  completionSchool: PropTypes.string,
  contentGroupCode: PropTypes.string.isRequired,
  courseNodeId: PropTypes.string,
  coursePointer: PropTypes.shape({
    href: PropTypes.string,
    method: PropTypes.string,
    query: PropTypes.object,
  }),
  instructorDataKey: PropTypes.string.isRequired,
  nextStepButtonLabel: PropTypes.string,
  nextStepButtonLabelRef: PropTypes.object,
  nextStepHeader: PropTypes.string,
  nextStepHeaderRef: PropTypes.object,
  nextStepRoute: PropTypes.string,
  nextStepRouteRef: PropTypes.object,
  refreshInstructorWithGet: PropTypes.bool,
  siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

export default MarkCompleteSectionContainer
